/* eslint-disable no-debugger, no-console */

import "regenerator-runtime/runtime";
import { UPLOAD_CHUNK_SIZE } from "../util/constants";
import { NUMBER_OF_FILES_PER_CHUNK, GCS_UPLOAD_MODE } from "../util/constants";
import { fileMixins } from "./fileLegacy";
import { folderMixins } from "./folder";

async function uploadResumable({
  url,
  file,
  contentType,
  chunkSize,
  onChunkUpload,
  signal,
  isCancelled,
  maxRetries = 3,
  baseBackoffMs = 200,
  maxBackoffMs = 2000
}) {
  const total = file.size;
  let start = 0;

  while (start < total) {
    if (isCancelled && isCancelled()) {
      return;
    }
    const end = Math.min(start + chunkSize, total) - 1;
    const chunk = file.slice(start, end + 1);
    let attempt = 0;
    let res = null;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      try {
        res = await fetch(url, {
          method: "PUT",
          headers: {
            "Content-Type": contentType,
            "Content-Range": `bytes ${start}-${end}/${total}`,
          },
          body: chunk,
          signal,
        });
      } catch (err) {
        if (signal?.aborted) {
          throw err;
        }
        attempt += 1;
        if (attempt > maxRetries) {
          throw err;
        }
        const backoff = Math.min(
          maxBackoffMs,
          baseBackoffMs * Math.pow(2, attempt - 1) + Math.random() * baseBackoffMs
        );
        await new Promise((resolve) => setTimeout(resolve, backoff));
        continue;
      }

      if (res.status === 500 || res.status === 502 || res.status === 503 || res.status === 504) {
        attempt += 1;
        if (attempt > maxRetries) {
          throw new Error(`GCS upload failed with status ${res.status}`);
        }
        const backoff = Math.min(
          maxBackoffMs,
          baseBackoffMs * Math.pow(2, attempt - 1) + Math.random() * baseBackoffMs
        );
        await new Promise((resolve) => setTimeout(resolve, backoff));
        continue;
      }
      break;
    }

    const isFinalChunk = end + 1 >= total;
    if (isFinalChunk && res.status === 308) {
      throw new Error("GCS upload did not complete; final status 308");
    }
    if (!isFinalChunk && (res.status === 200 || res.status === 201)) {
      throw new Error(`GCS upload completed before all bytes were sent with status ${res.status}`);
    }
    if (![200, 201, 308].includes(res.status)) {
      throw new Error(`GCS upload failed with status ${res.status}`);
    }

    onChunkUpload({
      totalBytes: total,
      uploadedBytes: end + 1,
      chunkIndex: Math.floor(start / chunkSize),
      chunkLength: chunk.size,
    });

    start = end + 1;
  }
}

function mapUploadError(error) {
  if (!error) {
    return { code: "UPLOAD_UNKNOWN", userMessage: "Upload failed. Please try again." };
  }
  if (error.name === "AbortError") {
    return { code: "UPLOAD_ABORTED", userMessage: "Upload cancelled." };
  }
  const message = error.message || String(error);
  if (message.includes("status 403") || message.includes("status 401")) {
    return { code: "UPLOAD_FORBIDDEN", userMessage: "Upload permission denied or URL expired." };
  }
  if (message.includes("status 404")) {
    return { code: "UPLOAD_URL_NOT_FOUND", userMessage: "Upload URL not found or expired." };
  }
  if (message.includes("status 500") || message.includes("status 502") || message.includes("status 503") || message.includes("status 504")) {
    return { code: "UPLOAD_TRANSIENT", userMessage: "Temporary upload failure. Please retry." };
  }
  return { code: "UPLOAD_FAILED", userMessage: "Upload failed. Please try again." };
}

let GcsUploadModule = null;
async function loadGcsUpload() {
  if (!GcsUploadModule) {
    const mod = await import("gcs-browser-upload");
    GcsUploadModule = mod.default || mod;
  }
  return GcsUploadModule;
}

function cancellable(asyncFn, cancel = (() => null)) {
  let cancelled = false;
  return {
    run: () => asyncFn(() => cancelled).then(result => { return { result, cancelled: cancelled} }),
    cancel: async () => {
      if (!cancelled) {
        cancelled = true;
        await cancel();
      }
    },
  };
}

export const uploadFileMixins = {
  mixins: [fileMixins, folderMixins],
  methods: {
    
    uploadFile({ file, ...options }, itemIndex, progressCallback) {
      let gcs = null;
      let id = null;

      let abortController = null;
      const cancel = async () => {
        if (id !== null) {
          gcs?.cancel();
          if (abortController) {
            abortController.abort();
          }
          await this.deleteFile({ id }).catch(console.error); // not a critical error, but leaves dead files
          id = null;
        }
      };

      return cancellable(async (isCancelled) => {
        abortController = new AbortController();
        try {
          const response = await this.createFile({ ...options, size: file.size, contentType: file.type });
          id = response.id;
          if (isCancelled()) {
            return;
          }
          if (file.size > 0) {
            if (GCS_UPLOAD_MODE === "library") {
              const GcsUpload = await loadGcsUpload();
              gcs = new GcsUpload({
                id: response.uploadId,
                url: response.uploadUrl,
                file,
                contentType: file.type,
                chunkSize: UPLOAD_CHUNK_SIZE,
                onChunkUpload: (info) => {
                  const fileProgress = itemIndex + (info.uploadedBytes / info.totalBytes);
                  if (fileProgress < itemIndex + 1) {
                    progressCallback(fileProgress, file.name);
                  }
                },
              });
              await gcs.start();
            } else {
              await uploadResumable({
                url: response.uploadUrl,
                file,
                contentType: file.type,
                chunkSize: UPLOAD_CHUNK_SIZE,
                signal: abortController.signal,
                isCancelled,
                maxRetries: 3,
                baseBackoffMs: 200,
                maxBackoffMs: 2000,
                onChunkUpload: (info) => {
                  const fileProgress = itemIndex + (info.uploadedBytes / info.totalBytes);
                  if (fileProgress < itemIndex + 1) {
                    progressCallback(fileProgress, file.name);
                  }
                },
              }).catch((err) => {
                console.error("GCS upload failed", {
                  name: file.name,
                  id,
                  uploadId: response.uploadId,
                  message: err?.message || String(err),
                  error: err,
                });
                if (isCancelled()) {
                  return;
                }
                throw err;
              });
            }
          }
          if (!isCancelled()) {
            await this.finaliseFile({id});
          }

          return {
            id,
            sensitiveNameDetection: response.sensitiveNameDetection || null,
          };

        } catch (error) {
          if (error?.name === "AbortError" || isCancelled()) {
            return;
          }
          // If someone is uploading an entire folder, we don't want to force them to have to deal with these manually!
          if (error.code !== 'DUPLICATE_ITEM') {
            await cancel();
            throw error;
          }
        } finally {
          id = null;
        }
      }, cancel);
    },
    chunkArray(inArray, chunkSize) {
      let returnArray = [];

      for (let i = 0;i<inArray.length;i+=chunkSize) {
        let currentChunk = inArray.slice(i, i + chunkSize);
        returnArray.push(currentChunk);
      }

      return returnArray;
    },
    async uploadFolderPaths(parentItem, paths, tags, metadata) {
      return await this.createPaths(parentItem, paths, tags, metadata);
    },
    buildUniqueFolderPaths(items) {
      const unique = (value, index, self) => self.indexOf(value) === index;
      return items
        .map((item) => {
          let parsedPath = item.subPath;
          if (parsedPath && parsedPath.endsWith(item.name)) {
            parsedPath = parsedPath.substring(0, parsedPath.lastIndexOf(item.name));
          }
          return parsedPath;
        })
        .filter(unique)
        .filter(String)
        .sort((a, b) => {
          const aDepth = a.split("/").filter(Boolean).length;
          const bDepth = b.split("/").filter(Boolean).length;
          if (aDepth !== bDepth) {
            return aDepth - bDepth;
          }
          return a.localeCompare(b);
        });
    },
    uploadChunk(chunk, chunkIndex, progressCallback, errorCallback) {
      let uploadFilePointers = [];
      
      return cancellable(async (isCancelled) => {

        let itemResults = [];
        if (isCancelled()) {
            return { chunkIndex, chunkSize: chunk.length, itemResults }
        }

        await Promise.all(chunk.map((item, itemIndex) => {

          // overall index across all files = offset for previous chunks + index within this chunk
          const overallIndex = (chunkIndex * NUMBER_OF_FILES_PER_CHUNK) + itemIndex;
          const uploadFile = this.uploadFile(item, overallIndex, progressCallback);        
          uploadFilePointers.push(uploadFile);
          
          const result = uploadFile.run()
            .then((uploadResult) => {
              progressCallback(overallIndex, "");
              return {
                itemIndex,
                success: true,
                sensitiveNameDetection: uploadResult?.result?.sensitiveNameDetection || null,
              };
            })
            .catch(error => {
              errorCallback(item, mapUploadError(error));
              return {itemIndex, success: false};
            });

          return result;
        }))
        .then((response) => {
          itemResults = response;
        })
        
        return { chunkIndex, numberOfFilesInChunk: chunk.length, itemResults }
      },
      () => uploadFilePointers.map(uploadFilePointer => uploadFilePointer?.cancel()));
    },
    uploadFiles(items, progressCallback, errorCallback) {
      let currentChunk = null;

      return cancellable(async (isCancelled) => {
        let anySuccess = false;
        let sensitiveDetections = [];
        
        if (items.length === 0) {
          return { anySuccess, sensitiveDetections };
        }

        const parentItem = items[0].parentItem;
        const tags = items[0].tags;
        const metadata = items[0].metadata;
        const allPaths = this.buildUniqueFolderPaths(items);
        const pathBatches = this.chunkArray(allPaths, 500);
        const totalPaths = allPaths.length;
        let pathsProcessed = 0;
        if (totalPaths > 0) {
          progressCallback(0, "", items.length, "creating-folders", {
            current: 0,
            total: totalPaths,
          });
        }
        for (let batchIndex = 0; batchIndex < pathBatches.length; batchIndex++) {
          const batch = pathBatches[batchIndex];
          const pathResponse = await this.uploadFolderPaths(parentItem, batch, tags, metadata);
          sensitiveDetections = sensitiveDetections.concat(
            (pathResponse?.sensitiveNameDetections || []).filter(detection => detection?.sensitiveNameDetected)
          );
          pathsProcessed += batch.length;
          if (totalPaths > 0) {
            progressCallback(0, "", items.length, "creating-folders", {
              current: pathsProcessed,
              total: totalPaths,
            });
          }
        }

        const itemsInChunks = this.chunkArray(items, NUMBER_OF_FILES_PER_CHUNK);
        const itemProgressCallback = (currentIndex, fileName) =>
          progressCallback(currentIndex, fileName, items.length, "uploading-files");
        
        for (let chunkIndex = 0; chunkIndex<itemsInChunks.length; chunkIndex++) {
        
          currentChunk = this.uploadChunk(itemsInChunks[chunkIndex], chunkIndex, itemProgressCallback, errorCallback)
          await currentChunk.run()
            .then(response => {
              const itemResults = response?.result?.itemResults || [];
              anySuccess = anySuccess || itemResults.filter(r => r?.success).length > 0;
              sensitiveDetections = sensitiveDetections.concat(
                itemResults
                  .map(r => r?.sensitiveNameDetection)
                  .filter(detection => detection?.sensitiveNameDetected)
              );
            })
            .catch(error => console.log("failed to process chunk", error));

          if (isCancelled()) {
            return;
          }
        }

        if (isCancelled()) {
          return false;
        }
        
        if (!anySuccess) {
           throw new Error('Failed to upload');
         }

        return { anySuccess, sensitiveDetections };
      }, () => currentChunk?.cancel());
    },
  },
};
